import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import { ResourceEmpty } from "../../../utils/ResourceEmpty";
import { ResourceError } from "../../../utils/ResourceError";
import { useNavigate } from "react-router-dom";
import { getProjectFoldersAsync } from "../../../../slices/project/folder";
import {
  getDocumentsAsync,
  loadMoreDocumentsAsync,
  deleteDocumentAsync,
  updateDocumentAsync,
} from "../../../../slices/document";
import { documentSrc } from "../../documents/utils";
import { 
  DocumentIcon, 
  TrashIcon, 
  PencilIcon, 
  EyeIcon,
  ArrowTopRightOnSquareIcon 
} from "@heroicons/react/24/outline";
import { PMDialog } from "../../../utils/PMDialog";
import { PMField } from "../../../utils/PMField";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";

export const DocumentsSection = (props) => {
  const filters = props.filters ? props.filters : [];
  const { project, refresh, isAdmin = false } = props;

  const dispatch = useDispatch();
  const { t } = useTranslation();

  // State for admin functionality
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);

  const itemState = useSelector((store) => {
    return store.documents;
  });

  const { isLoading, data, error, errors, totalCount } = itemState;

  const [controller, setController] = useState({
    filters: [...filters],
    page: 1,
    query: "",
    sort: "desc",
    sortBy: "updated_at",
    per_page: 10,
    view: "all",
  });

  useEffect(() => {
    const payload = {
      filters: controller.filters,
      page: controller.page,
      query: controller.query,
      sort: controller.sort,
      sortBy: controller.sortBy,
      view: controller.view,
    };
    if (controller.page === 1) {
      dispatch(getDocumentsAsync(payload));
    } else if (controller.page > 1) {
      dispatch(loadMoreDocumentsAsync(payload));
    }
  }, [dispatch, controller, refresh]);

  const navigate = useNavigate();

  const handlePageChange = (newPage) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const hasMorePages = () => {
    return controller.page * controller.per_page < totalCount;
  };

  // Admin handlers
  const handleEditDocument = (document) => {
    setSelectedDocument(document);
    setEditDialogOpen(true);
  };

  const handleDeleteDocument = (document) => {
    setDocumentToDelete(document);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (documentToDelete) {
      try {
        await dispatch(deleteDocumentAsync(documentToDelete.id));
        toast.success("Dokument slettet");
        setDeleteConfirmOpen(false);
        setDocumentToDelete(null);
        // Refresh the documents list
        dispatch(getDocumentsAsync(controller));
      } catch (error) {
        toast.error("Feil ved sletting av dokument");
      }
    }
  };

  const handleUpdateDocument = async (values) => {
    try {
      await dispatch(updateDocumentAsync(values));
      toast.success("Dokument oppdatert");
      setEditDialogOpen(false);
      setSelectedDocument(null);
      // Refresh the documents list
      dispatch(getDocumentsAsync(controller));
    } catch (error) {
      toast.error("Feil ved oppdatering av dokument");
    }
  };

  if (isLoading)
    return (
      <div className="mt-10">
        <ResourceLoading />
      </div>
    );
  if (error) return <ResourceError errors={errors} />;
  if (!data.length > 0) return <ResourceEmpty />;

  const handleDocumentOnClick = (document) => {
    // Open document in new tab using actual file URL
    if (document?.file?.url) {
      window.open(document.file.url, "_blank");
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center space-x-3">
          <div className="h-8 w-8 bg-blue-100 rounded-lg flex items-center justify-center">
            <DocumentIcon className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Dokumenter</h3>
            <p className="text-sm text-gray-500">
              {data.length} {data.length === 1 ? 'dokument' : 'dokumenter'}
            </p>
          </div>
        </div>
      </div>

      {/* Documents Grid */}
      <div className="p-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
          {data?.map((item) => (
            <div
              key={item.id}
              className="group relative bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-200 overflow-hidden"
            >
              {/* Document Preview */}
              <div 
                className="aspect-square bg-gray-50 cursor-pointer relative overflow-hidden"
                onClick={() => handleDocumentOnClick(item)}
              >
                <img
                  src={documentSrc(item, "thumb")}
                  alt={item.title || "Dokument"}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                />
                
                {/* Overlay on hover */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full p-2 transition-all duration-200 transform scale-75 group-hover:scale-100">
                    <EyeIcon className="h-4 w-4 text-gray-700" />
                  </div>
                </div>
              </div>

              {/* Document Info */}
              <div className="p-3">
                <h4 className="text-xs font-medium text-gray-900 truncate mb-1" title={item.title}>
                  {item.title || "Uten tittel"}
                </h4>
                <p className="text-xs text-gray-500 truncate mb-2" title={item?.file?.filename}>
                  {item?.file?.filename || "Ukjent fil"}
                </p>
                {item?.size && (
                  <p className="text-xs text-gray-400 truncate">
                    {item.size}
                  </p>
                )}
              </div>

              {/* Admin Actions */}
              {isAdmin && (
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <div className="flex space-x-1">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditDocument(item);
                      }}
                      className="p-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-full transition-colors duration-200"
                      title="Rediger dokument"
                    >
                      <PencilIcon className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteDocument(item);
                      }}
                      className="p-1.5 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors duration-200"
                      title="Slett dokument"
                    >
                      <TrashIcon className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Load More Button */}
        {hasMorePages() && (
          <div className="mt-6 flex justify-center">
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
              onClick={() => handlePageChange(controller.page + 1)}
            >
              <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-2" />
              Vis flere dokumenter
            </button>
          </div>
        )}
      </div>

      {/* Edit Document Dialog */}
      <PMDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        title="Rediger dokument"
      >
        {selectedDocument && (
          <Formik
            initialValues={{
              id: selectedDocument.id,
              title: selectedDocument.title || "",
              submit: null,
            }}
            validationSchema={Yup.object({
              title: Yup.string().required("Tittel er påkrevd"),
            })}
            onSubmit={handleUpdateDocument}
          >
            {(formik) => (
              <Form>
                <div className="space-y-4">
                  <PMField
                    name="title"
                    type="text"
                    label="Tittel"
                    {...formik}
                  />
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    onClick={() => setEditDialogOpen(false)}
                  >
                    Avbryt
                  </button>
                  <button
                    type="submit"
                    disabled={formik.isSubmitting}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400"
                  >
                    {formik.isSubmitting ? "Lagrer..." : "Lagre"}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </PMDialog>

      {/* Delete Confirmation Dialog */}
      <PMDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        title="Bekreft sletting"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Er du sikker på at du vil slette dokumentet &quot;{documentToDelete?.title}&quot;?
          </p>
          <p className="text-xs text-gray-500">
            Denne handlingen kan ikke angres.
          </p>
          
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={() => setDeleteConfirmOpen(false)}
            >
              Avbryt
            </button>
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              onClick={confirmDelete}
            >
              Slett
            </button>
          </div>
        </div>
      </PMDialog>
    </div>
  );
};

DocumentsSection.defaultProps = {
  page: 1,
  itemState: [],
  itemsCount: 0,
  selectedItems: [],
  sort: "desc",
  sortBy: "createdAt",
};
